import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  ArrowLeft, Copy, Plus, Loader2, RefreshCw, Ticket,
  Check, X as XIcon, AlertCircle, QrCode, Download, Trash2,
} from 'lucide-react';
import { supabase } from '@/services/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { AccountMemberRole } from '@asi/shared';

interface AccountInvite {
  id: string;
  account_id: string;
  code: string;
  role: AccountMemberRole;
  max_uses: number;
  used_count: number;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
}

interface AccountInfo {
  id: string;
  name: string;
  account_number: string;
}

/** Generates an 8-char uppercase alphanumeric code, no ambiguous chars. */
function generateCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no I/O/0/1
  let out = '';
  for (let i = 0; i < 8; i++) {
    out += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return out;
}

const ROLE_OPTIONS: { value: AccountMemberRole; label: string }[] = [
  { value: 'worker',     label: 'Empleado' },
  { value: 'manager',    label: 'Encargado' },
  { value: 'technician', label: 'Técnico' },
  { value: 'familiar',   label: 'Familiar' },
  // 'admin' y 'owner' se asignan solo via QR de administrador (AdminOnboardingQR)
];

export function InvitesPage({ accountId, embedded = false }: { accountId?: string; embedded?: boolean } = {}) {
  const params = useParams<{ id: string }>();
  const id = accountId ?? params.id;
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [invites, setInvites] = useState<AccountInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  // Create form state
  const [creating, setCreating]   = useState(false);
  const [newRole, setNewRole]     = useState<AccountMemberRole>('worker');
  const [newMaxUses, setNewMaxUses] = useState(10);
  const [newExpiresDays, setNewExpiresDays] = useState(7);
  const [createError, setCreateError] = useState<string | null>(null);
  const [qrInvite, setQrInvite]   = useState<AccountInvite | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [accRes, invRes] = await Promise.all([
        supabase.from('accounts').select('id, name, account_number').eq('id', id).single(),
        supabase.from('account_invites').select('*').eq('account_id', id).order('created_at', { ascending: false }),
      ]);
      if (accRes.error) throw accRes.error;
      if (invRes.error) throw invRes.error;
      setAccount(accRes.data as AccountInfo);
      setInvites((invRes.data ?? []) as AccountInvite[]);
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? 'Error cargando invitaciones');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  const createInvite = async () => {
    if (!id) return;
    setCreating(true);
    setCreateError(null);
    try {
      const { data: userRes } = await supabase.auth.getUser();
      const code = generateCode();
      const expires_at = newExpiresDays > 0
        ? new Date(Date.now() + newExpiresDays * 24 * 60 * 60 * 1000).toISOString()
        : null;
      const { error: insErr } = await supabase
        .from('account_invites')
        .insert({
          account_id: id,
          code,
          role: newRole,
          max_uses: Math.max(1, newMaxUses),
          expires_at,
          created_by: userRes.user?.id ?? null,
        });
      if (insErr) throw insErr;
      await load();
    } catch (e: unknown) {
      setCreateError((e as { message?: string })?.message ?? 'Error creando invitación');
    } finally {
      setCreating(false);
    }
  };

  const deleteInvite = async (inv: AccountInvite) => {
    const label = inv.used_count > 0
      ? `Este código ya fue usado ${inv.used_count} vez(es). ¿Eliminar de todos modos?`
      : `¿Eliminar el código ${inv.code}? Esta acción no se puede deshacer.`;
    if (!window.confirm(label)) return;
    const { error: delErr } = await supabase
      .from('account_invites')
      .delete()
      .eq('id', inv.id);
    if (delErr) {
      alert(`Error: ${delErr.message}`);
      return;
    }
    await load();
  };

  const toggleActive = async (inv: AccountInvite) => {
    const { error: updErr } = await supabase
      .from('account_invites')
      .update({ is_active: !inv.is_active })
      .eq('id', inv.id);
    if (updErr) {
      alert(`Error: ${updErr.message}`);
      return;
    }
    await load();
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {/* could toast here */},
      (err) => alert(`No se pudo copiar: ${err}`),
    );
  };

  const downloadQR = (code: string) => {
    const svg = document.getElementById(`qr-svg-${code}`);
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `invitacion-${code}.svg`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={embedded ? '' : 'container mx-auto p-6 max-w-5xl'}>
      {!embedded && (
        <div className="flex items-center gap-3 mb-6">
          <Link to={id ? `/accounts/${id}` : '/accounts'}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Volver
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Ticket className="w-6 h-6" /> Códigos de invitación
            </h1>
            {account && (
              <p className="text-sm text-muted-foreground mt-1">
                {account.account_number} · {account.name}
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      )}

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Plus className="w-4 h-4" /> Generar nuevo código
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Rol asignado</label>
              <select
                className="mt-1 w-full h-10 rounded-md border bg-background px-3 text-sm"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as AccountMemberRole)}
              >
                {ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Usos máximos</label>
              <Input
                type="number"
                min={1}
                value={newMaxUses}
                onChange={(e) => setNewMaxUses(Number(e.target.value) || 1)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Vence en (días, 0=nunca)</label>
              <Input
                type="number"
                min={0}
                value={newExpiresDays}
                onChange={(e) => setNewExpiresDays(Number(e.target.value) || 0)}
              />
            </div>
            <Button onClick={() => void createInvite()} disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Generar código
            </Button>
          </div>
          {createError && (
            <p className="text-xs text-destructive mt-3 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {createError}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Códigos existentes ({invites.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : invites.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Aún no has generado códigos.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-3">Código</th>
                    <th className="py-2 pr-3">Rol</th>
                    <th className="py-2 pr-3">Usos</th>
                    <th className="py-2 pr-3">Vence</th>
                    <th className="py-2 pr-3">Estado</th>
                    <th className="py-2 pr-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => {
                    const expired = inv.expires_at ? new Date(inv.expires_at) < new Date() : false;
                    const exhausted = inv.used_count >= inv.max_uses;
                    const usable = inv.is_active && !expired && !exhausted;
                    return (
                      <tr key={inv.id} className="border-b last:border-b-0">
                        <td className="py-3 pr-3">
                          <div className="flex items-center gap-2">
                            <code className="font-mono font-bold text-base tracking-widest">{inv.code}</code>
                            <Button variant="ghost" size="sm" onClick={() => copy(inv.code)} title="Copiar código">
                              <Copy className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setQrInvite(inv)} title="Ver QR">
                              <QrCode className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                        <td className="py-3 pr-3">
                          <Badge variant="outline">
                            {ROLE_OPTIONS.find((r) => r.value === inv.role)?.label ?? inv.role}
                          </Badge>
                        </td>
                        <td className="py-3 pr-3 tabular-nums">{inv.used_count} / {inv.max_uses}</td>
                        <td className="py-3 pr-3 text-xs">
                          {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="py-3 pr-3">
                          {usable ? (
                            <Badge className="bg-green-500/10 text-green-600">Activo</Badge>
                          ) : !inv.is_active ? (
                            <Badge variant="outline">Desactivado</Badge>
                          ) : expired ? (
                            <Badge className="bg-amber-500/10 text-amber-600">Vencido</Badge>
                          ) : (
                            <Badge className="bg-gray-500/10 text-gray-500">Agotado</Badge>
                          )}
                        </td>
                        <td className="py-3 pr-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => void toggleActive(inv)}>
                              {inv.is_active ? <XIcon className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                              <span className="ml-1 text-xs">{inv.is_active ? 'Desactivar' : 'Activar'}</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => void deleteInvite(inv)}
                              title="Eliminar código"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* QR Modal */}
      {qrInvite && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setQrInvite(null)}
        >
          <div
            className="bg-background border rounded-2xl p-8 flex flex-col items-center gap-4 shadow-2xl max-w-xs w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold">Código de invitación</h2>
            <p className="text-sm text-muted-foreground text-center">
              Comparte este QR para que el nuevo miembro se una desde la app.
            </p>
            <QRCodeSVG
              id={`qr-svg-${qrInvite.code}`}
              value={`asiclient://join?code=${qrInvite.code}`}
              size={200}
              level="M"
              includeMargin
            />
            <code className="font-mono font-bold text-2xl tracking-[0.3em]">{qrInvite.code}</code>
            <p className="text-xs text-muted-foreground text-center">
              {ROLE_OPTIONS.find((r) => r.value === qrInvite.role)?.label} ·{' '}
              {qrInvite.used_count}/{qrInvite.max_uses} usos
              {qrInvite.expires_at ? ` · vence ${new Date(qrInvite.expires_at).toLocaleDateString()}` : ''}
            </p>
            <div className="flex flex-col gap-2 w-full">
              <Button
                variant="outline" className="w-full"
                onClick={() => copy(`asiclient://join?code=${qrInvite.code}`)}
              >
                <Copy className="w-4 h-4 mr-2" /> Copiar enlace
              </Button>
              <Button
                variant="outline" className="w-full"
                onClick={() => downloadQR(qrInvite.code)}
              >
                <Download className="w-4 h-4 mr-2" /> Descargar QR
              </Button>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setQrInvite(null)}>
              <XIcon className="w-4 h-4 mr-1" /> Cerrar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
